// components/collab-projects/CollabProjectTree.jsx
import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiChevronRight, FiUser } from "react-icons/fi";
import Avatar from "../retro/avatar/avatar.component";
import TinyContextMenu from "../tiny-context-menu/TinyContextMenu";
import {
  projectKey,
  projectUrl,
  useCollabProjects,
} from "./CollabProjectsProvider";
import "./collab-projects.scss";

const alphabetical = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
  });

export function CollabProjectsFilter() {
  const { showWorkspaceProjects, setShowWorkspaceProjects } =
    useCollabProjects();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  return (
    <span className="collab-filter">
      <TinyContextMenu
        customBtn={
          <span
            className="collab-filter__dots"
            aria-label="Collab project options"
          >
            ⋮
          </span>
        }
        items={[
          {
            key: "workspace-projects",
            label: showWorkspaceProjects
              ? "Hide workspace projects"
              : "Show workspace projects",
            icon: <FiUser />,
            disabled: saving,
            onPress: async () => {
              setSaving(true);
              setError("");

              try {
                await setShowWorkspaceProjects(!showWorkspaceProjects);
              } catch {
                setError("Could not save your filter. Please try again.");
              } finally {
                setSaving(false);
              }
            },
          },
        ]}
      />

      {error && (
        <span className="collab-filter__error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

export default function CollabProjectTree({ onNavigate, heading = true }) {
  const { projects, people, loading, error } = useCollabProjects();

  const [open, setOpen] = useState(true);
  const [closed, setClosed] = useState({});

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const groups = useMemo(() => {
    const map = new Map();

    projects.forEach((project) => {
      const uid = project.sharedBy || "";

      if (!map.has(uid)) {
        map.set(uid, {
          uid,
          user: people[uid],
          name:
            people[uid]?.userName ||
            (uid ? "Unknown user" : "Shared projects"),
          projects: [],
        });
      }

      map.get(uid).projects.push(project);
    });

    return [...map.values()]
      .sort((a, b) => alphabetical(a.name, b.name))
      .map((group) => ({
        ...group,
        projects: group.projects.sort(
          (a, b) =>
            alphabetical(a.name, b.name) ||
            alphabetical(a.stationId, b.stationId),
        ),
      }));
  }, [projects, people]);

  return (
    <section className="collab-tree" aria-label="Collab Projects">
      {heading && (
        <div className="collab-tree__header-row">
          <button
            type="button"
            className="collab-tree__heading"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <span className="collab-dot" aria-hidden="true" />
            <strong>Collab Projects</strong>
          </button>

          <CollabProjectsFilter />

          <button
            type="button"
            className="collab-tree__toggle"
            aria-label={
              open ? "Collapse Collab Projects" : "Expand Collab Projects"
            }
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <FiChevronRight className={open ? "is-open" : ""} />
          </button>
        </div>
      )}

      {(!heading || open) && (
        <>
          {error && (
            <p className="collab-tree__status" role="status">
              {error}
            </p>
          )}

          {loading && !projects.length && (
            <p className="collab-tree__status" role="status">
              Loading projects…
            </p>
          )}

          {!loading && !projects.length && (
            <p className="collab-tree__status">No collab projects yet.</p>
          )}

          <ul className="collab-tree__people">
            {groups.map((group) => (
              <li
                key={group.uid}
                className="collab-tree__branch collab-tree__person"
              >
                <div className="collab-tree__person-row">
                  <button
                    type="button"
                    className="collab-tree__person-button"
                    aria-expanded={!closed[group.uid]}
                    onClick={() =>
                      setClosed((state) => ({
                        ...state,
                        [group.uid]: !state[group.uid],
                      }))
                    }
                  >
                    <span className="collab-tree__avatar" aria-hidden="true">
                      {group.user ? (
                        <Avatar user={group.user} size={28} tiny />
                      ) : (
                        <FiUser size={24} />
                      )}
                    </span>

                    <span className="collab-tree__name">
                      {group.uid && <small>Added by</small>}
                      <span>{group.name}</span>
                    </span>
                  </button>

                  {group.user && (
                    <TinyContextMenu
                      items={[
                        {
                          key: "profile",
                          label: "Show profile",
                          icon: <FiUser />,
                          onPress: () => {
                            onNavigate?.();

                            navigate(`/profile/${group.uid}`, {
                              state: { user: group.user },
                            });
                          },
                        },
                      ]}
                    />
                  )}

                  <button
                    type="button"
                    className="collab-tree__toggle"
                    aria-label={`${
                      closed[group.uid] ? "Expand" : "Collapse"
                    } ${group.name}`}
                    aria-expanded={!closed[group.uid]}
                    onClick={() =>
                      setClosed((state) => ({
                        ...state,
                        [group.uid]: !state[group.uid],
                      }))
                    }
                  >
                    <FiChevronRight
                      className={!closed[group.uid] ? "is-open" : ""}
                    />
                  </button>
                </div>

                {!closed[group.uid] && (
                  <ul className="collab-tree__projects">
                    {group.projects.map((project) => {
                      const url = projectUrl(project);
                      const selected = pathname.startsWith(url.slice(0, -1));

                      return (
                        <li
                          className="collab-tree__branch"
                          key={projectKey(
                            project.spaceId,
                            project.stationId,
                          )}
                        >
                          <Link
                            to={url}
                            className={`collab-tree__project${
                              selected ? " is-selected" : ""
                            }`}
                            aria-current={selected ? "page" : undefined}
                            onClick={onNavigate}
                          >
                            <span className="collab-tree__emoji">
                              {project.emojicon || "📋"}
                            </span>

                            <span title={project.name}>
                              {project.name || "Untitled project"}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}